import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

import { NavigationLink } from "../Navigation/NavigationMenu/NavigationLink";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { cn } from "@/lib/utils";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { AUTH_CONFIG } from "@/constants";
import { SETTINGS_TABS } from "@/routes/settings/constants";

interface SettingsNavigationProps {
  onBack: () => void;
}

export const SettingsNavigation = ({ onBack }: SettingsNavigationProps) => {
  const location = useLocation();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [modelApiKeysDropdownOpen, setModelApiKeysDropdownOpen] = useState(false);

  // Slack (Wrangler) is a cloud-only feature; hide its tab on on-prem deployments.
  const visibleTabs = useMemo(
    () => SETTINGS_TABS.filter(tab => !(AUTH_CONFIG.isOnPrem && tab.path === "slack")),
    [],
  );

  const groups = useMemo(() => {
    const grouped: Record<string, typeof SETTINGS_TABS> = {};
    visibleTabs.forEach(tab => {
      const group = tab.group || "Other";
      if (!grouped[group]) {
        grouped[group] = [];
      }
      grouped[group].push(tab);
    });
    return grouped;
  }, [visibleTabs]);

  const isActive = (path: string) => location.pathname.includes(`/settings/${path}`);

  // Auto-expand items with active sub-tabs
  useEffect(() => {
    SETTINGS_TABS.forEach(tab => {
      if (tab.subTabs) {
        const hasActiveSub = tab.subTabs.some(subTab =>
          location.pathname.includes(`/settings/${tab.path}/${subTab.path}`),
        );
        if (hasActiveSub) {
          setExpandedItems(prev => new Set(prev).add(tab.name));
        }
      }
    });
  }, [location.pathname]);

  const toggleExpanded = (name: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  if (isCollapsed) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex justify-center border-b py-2">
          <BasicTooltip content="Go Back">
            <Button variant="ghost" size="icon" className="h-10 w-10" onClick={onBack}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </BasicTooltip>
        </div>

        <div className="flex flex-1 flex-col items-center gap-1 overflow-y-auto px-2 py-2">
          {visibleTabs.map(item => {
            const hasSubTabs = item.subTabs && item.subTabs.length > 0;
            const itemActive = isActive(item.path);

            if (hasSubTabs) {
              const isSubActive = item.subTabs!.some(sub => isActive(`${item.path}/${sub.path}`));

              return (
                <SidebarMenuItem key={item.name}>
                  <DropdownMenu
                    open={modelApiKeysDropdownOpen}
                    onOpenChange={setModelApiKeysDropdownOpen}
                  >
                    <BasicTooltip content={item.name} side="right">
                      <DropdownMenuTrigger asChild>
                        <div
                          className="relative"
                          onMouseEnter={() => setModelApiKeysDropdownOpen(true)}
                        >
                          <SidebarMenuButton
                            isActive={itemActive || isSubActive}
                            className="mx-auto h-10 w-10 justify-center p-0 text-gray-800 transition-all duration-200 hover:bg-gray-50/80 [&_svg]:text-gray-800"
                          >
                            <span className="flex h-4 w-4 shrink-0 items-center justify-center [&_img]:!h-5 [&_img]:!w-5 [&_svg]:!h-5 [&_svg]:!w-5">
                              {item.outlineIcon}
                            </span>
                          </SidebarMenuButton>
                        </div>
                      </DropdownMenuTrigger>
                    </BasicTooltip>
                    <DropdownMenuContent
                      side="right"
                      align="start"
                      className="w-56 border p-1 shadow-lg duration-200 animate-in slide-in-from-left-2"
                      onMouseLeave={() => setModelApiKeysDropdownOpen(false)}
                    >
                      {item.subTabs!.map(subTab => (
                        <NavigationLink
                          key={subTab.name}
                          to={`settings/${item.path}/${subTab.path}`}
                        >
                          <DropdownMenuItem
                            className="cursor-pointer gap-3 px-3 py-2 text-sm text-gray-800 transition-colors duration-150 hover:bg-gray-50/50 hover:text-gray-900"
                            title={
                              (subTab as { panelDescription?: string }).panelDescription ??
                              subTab.name
                            }
                          >
                            <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center opacity-60 [&_img]:!h-3.5 [&_img]:!w-3.5 [&_svg]:!h-3.5 [&_svg]:!w-3.5">
                              {subTab.outlineIcon}
                            </span>
                            <span>{subTab.name}</span>
                          </DropdownMenuItem>
                        </NavigationLink>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </SidebarMenuItem>
              );
            }

            return (
              <SidebarMenuItem key={item.name}>
                <BasicTooltip content={item.name} side="right">
                  <span className="inline-flex w-full justify-center">
                    <NavigationLink to={`settings/${item.path}`}>
                      <SidebarMenuButton
                        isActive={itemActive}
                        className="mx-auto h-10 w-10 justify-center p-0 text-gray-800 transition-all duration-200 hover:bg-gray-50/80 [&_svg]:text-gray-800"
                      >
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center [&_img]:!h-5 [&_img]:!w-5 [&_svg]:!h-5 [&_svg]:!w-5">
                          {item.outlineIcon}
                        </span>
                      </SidebarMenuButton>
                    </NavigationLink>
                  </span>
                </BasicTooltip>
              </SidebarMenuItem>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-1 border-b border-gray-200 bg-white px-2 py-1.5">
        <BasicTooltip content="Go Back">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
            onClick={onBack}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </BasicTooltip>
        <span className="text-sm font-semibold">Settings</span>
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-2">
        {Object.entries(groups).map(([groupName, items]) => (
          <div key={groupName} className="mb-3">
            <div className="py-1 pl-1 pr-3 text-xs font-medium tracking-wider text-gray-400">
              {groupName}
            </div>
            <SidebarMenu className="space-y-0.5">
              {items.map(item => {
                const hasSubTabs = item.subTabs && item.subTabs.length > 0;
                const isExpanded = expandedItems.has(item.name);
                const itemActive = isActive(item.path);

                return (
                  <div key={item.name}>
                    <SidebarMenuItem>
                      {hasSubTabs ? (
                        <BasicTooltip content={item.panelDescription ?? item.name}>
                          <SidebarMenuButton
                            isActive={itemActive}
                            className="w-full justify-between text-gray-800 transition-all duration-200 hover:translate-x-0.5 [&_svg]:text-gray-800"
                            onClick={() => toggleExpanded(item.name)}
                          >
                            <div className="flex items-center gap-2">
                              <span className="flex h-4 w-4 shrink-0 items-center justify-center [&_img]:!h-5 [&_img]:!w-5 [&_svg]:!h-5 [&_svg]:!w-5">
                                {item.outlineIcon}
                              </span>
                              <span className="font-medium">{item.name}</span>
                            </div>
                            {isExpanded ? (
                              <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                            )}
                          </SidebarMenuButton>
                        </BasicTooltip>
                      ) : (
                        <BasicTooltip content={item.panelDescription ?? item.name}>
                          <NavigationLink to={`settings/${item.path}`}>
                            <SidebarMenuButton
                              isActive={itemActive}
                              className="text-gray-800 transition-all duration-200 hover:translate-x-0.5 [&_svg]:text-gray-800"
                            >
                              <span className="flex h-4 w-4 shrink-0 items-center justify-center [&_img]:!h-5 [&_img]:!w-5 [&_svg]:!h-5 [&_svg]:!w-5">
                                {item.outlineIcon}
                              </span>
                              <span className="font-medium">{item.name}</span>
                            </SidebarMenuButton>
                          </NavigationLink>
                        </BasicTooltip>
                      )}
                    </SidebarMenuItem>

                    {hasSubTabs && (
                      <div
                        className={cn(
                          "grid transition-[grid-template-rows] duration-200 ease-out",
                          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                        )}
                      >
                        <div className="overflow-hidden">
                          <div className="ml-4 mt-0.5 space-y-0.5">
                            {item.subTabs!.map(subTab => (
                              <SidebarMenuItem key={subTab.name}>
                                <BasicTooltip
                                  content={
                                    (subTab as { panelDescription?: string }).panelDescription ??
                                    subTab.name
                                  }
                                >
                                  <NavigationLink to={`settings/${item.path}/${subTab.path}`}>
                                    <SidebarMenuButton
                                      isActive={isActive(`${item.path}/${subTab.path}`)}
                                      className="ml-2 text-gray-800 transition-all duration-200 hover:translate-x-0.5 hover:bg-gray-50/50 [&_svg]:text-gray-800"
                                    >
                                      <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center [&_img]:!h-3.5 [&_img]:!w-3.5 [&_svg]:!h-3.5 [&_svg]:!w-3.5">
                                        {subTab.outlineIcon}
                                      </span>
                                      <span className="text-gray-800">{subTab.name}</span>
                                    </SidebarMenuButton>
                                  </NavigationLink>
                                </BasicTooltip>
                              </SidebarMenuItem>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </SidebarMenu>
          </div>
        ))}
      </div>
    </div>
  );
};
